import React, { useState, useEffect, useRef } from 'react';
import CircularProgress from './CircularProgress';
import audioManager from '../../utils/audio';
import wakeLockManager from '../../utils/wakeLock';

const AMRAPTimer = ({ capSec = 1200, exercises = [], onComplete, onStop }) => {
  const [timeRemaining, setTimeRemaining] = useState(capSec);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [rounds, setRounds] = useState(0);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  const totalSeconds = capSec;
  const progress = (timeRemaining / totalSeconds) * 100;

  // Get current exercise based on rounds completed
  const getCurrentExercise = () => {
    if (!exercises || exercises.length === 0) return 'AMRAP';
    const exerciseIndex = rounds % exercises.length;
    const exercise = exercises[exerciseIndex];
    return typeof exercise === 'string' ? exercise : exercise.name || 'Exercise';
  };

  const exerciseName = getCurrentExercise();

  useEffect(() => {
    if (isRunning && !isPaused) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          const newTime = prev - 1;

          if (newTime === 60 || newTime === 30 || newTime === 10) {
            audioManager.announceTime(newTime);
          }

          if (newTime <= 3 && newTime > 0) {
            audioManager.countdownBeep(newTime);
          }

          if (newTime <= 0) {
            clearInterval(intervalRef.current);
            setIsRunning(false);
            audioManager.completionSound();
            audioManager.speak('Time!');
            wakeLockManager.release();
            if (onComplete) onComplete();
            return 0;
          }

          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, isPaused, onComplete]);

  const handleStart = () => {
    audioManager.init();
    wakeLockManager.request();

    setCountdown(3);
    audioManager.speak('3');

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) return null;
        const next = prev - 1;
        if (next > 0) audioManager.speak(String(next));
        else if (next === 0) audioManager.speak('Go!');
        if (next < 0) {
          clearInterval(countdownRef.current);
          setCountdown(null);
          setIsRunning(true);
          return null;
        }
        return next;
      });
    }, 1000);
  };

  const handleToggle = () => {
    if (countdown !== null) return;

    if (!isRunning) {
      handleStart();
    } else if (isPaused) {
      setIsPaused(false);
      audioManager.speak('Go');
    } else {
      setIsPaused(true);
      audioManager.speak('Paused');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  if (countdown !== null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div
          key={countdown}
          className="text-gray-900 dark:text-white font-black tabular-nums"
          style={{
            fontSize: 'clamp(15rem, 40vw, 30rem)',
            animation: 'scaleOut 1s ease-out'
          }}
        >
          {countdown === 0 ? 'GO' : countdown}
        </div>
        <style>{`
          @keyframes scaleOut {
            0% { transform: scale(0.5); opacity: 1; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1.15); opacity: 0.3; }
          }
        `}</style>
      </div>
    );
  }

  const handleStop = () => {
    if (window.confirm('Are you sure you want to stop the workout?')) {
      if (onStop) onStop();
    }
  };

  return (
    <div className="flex flex-col items-center pt-16 min-h-screen bg-gray-50 dark:bg-gray-900 px-4">
      {/* Title and current exercise at top */}
      <div className="text-center mb-6">
        <div className="text-3xl font-bold text-gray-900 dark:text-white uppercase tracking-wide">
          {Math.floor(capSec / 60)} MIN AMRAP
        </div>
        {isRunning && exercises && exercises.length > 0 && (
          <div className="text-xl font-semibold text-gray-600 dark:text-gray-400 mt-2">
            {exerciseName}
          </div>
        )}
      </div>

      {/* Circular Timer with play button inside when not running */}
      <div className="relative">
        <CircularProgress progress={progress} size={320} strokeWidth={16} color="#3b82f6">
          <div className="flex flex-col items-center relative">
            {!isRunning ? (
              <button
                onClick={handleToggle}
                className="w-32 h-32 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center shadow-xl transition-transform active:scale-95"
              >
                <svg className="w-16 h-16 text-white ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </button>
            ) : (
              <>
                <div className="text-7xl font-black tabular-nums leading-none text-gray-900 dark:text-white">
                  {formatTime(timeRemaining)}
                </div>
                <div className="absolute bottom-[-2.5rem] left-1/2 transform -translate-x-1/2 text-xl text-gray-500 dark:text-gray-400">
                  {isPaused ? 'PAUSED' : 'remaining'}
                </div>
              </>
            )}
          </div>
        </CircularProgress>
      </div>

      {/* Rounds counter */}
      <div className="my-8 flex items-center gap-6">
        <div className="text-center">
          <div className="text-5xl font-black text-gray-900 dark:text-white">{rounds}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">ROUNDS</div>
        </div>
        {isRunning && (
          <button
            onClick={() => setRounds(rounds + 1)}
            className="w-16 h-16 rounded-full bg-green-600 hover:bg-green-700 flex items-center justify-center shadow-lg transition-transform active:scale-95"
          >
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>

      {/* Control buttons - only show when running */}
      {isRunning && (
        <div className="flex gap-6">
          <button
            onClick={handleStop}
            className="w-16 h-16 rounded-full bg-gray-700 dark:bg-gray-600 hover:bg-gray-800 dark:hover:bg-gray-500 flex items-center justify-center shadow-lg transition-transform active:scale-95"
          >
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
              <line x1="8" y1="8" x2="16" y2="16" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <button
            onClick={handleToggle}
            className="w-16 h-16 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center shadow-lg transition-transform active:scale-95"
          >
            {isPaused ? (
              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AMRAPTimer;
